import { FC, useState, useEffect } from "react";
import { Button, Radio, RadioGroup, Stack, Text, Select } from "@mantine/core";
import { Questionnaire1 } from "./Questionnaire1";
import { Questionnaire2 } from "./Questionnaire2";
import { useTranslation } from "react-i18next";
import useQuestionnaireStore from "../../store/useQuestionnaireStore";
import { sendAnswers } from "../../api/userApi";
import useAuthStore from "../../store/useAuthStore";
import { getProfileLanguages, setProfileLanguage } from "../../api/profileApi";
import useReportStore from "../../store/useReportStore";
import useProfileStore from "../../store/useProfileStore";


interface Props {
  onSubmit: () => void;
}

export const QuestionnaireForm: FC<Props> = ({ onSubmit }) => {
  const { t, i18n } = useTranslation();
  const { access_token, setIsLoggedIn } = useAuthStore();
  const { fetchProfile, profileLanguage, getProfileLanguage } =
    useProfileStore();

  const [questionnaire, setQuestionnaire] = useState<{
    [key: string]: { answer_ids: number[]; text_answer?: string };
  }>({});
  const [errorQues, setErrorQues] = useState(false);
  const [loading, setLoading] = useState(false);
  const { questions, fetchQuestions } = useQuestionnaireStore();
  const [disabled, setDisabled] = useState(true);
  const { error, makeReport } = useReportStore();

  const [languages, setLanguages] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");

  useEffect(() => {
    if (access_token) {
      fetchQuestions(access_token);
    }
  }, [profileLanguage]);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const data = await getProfileLanguages();

        const languageOptions = Object.keys(data.data).map((key) => ({
          value: key,
          label: data.data[key],
        }));

        setLanguages(languageOptions);

        const currentLang = i18n.language;

        const currentLanguageOption = languageOptions.find(
          (option) => option.value === currentLang
        );

        if (currentLanguageOption) {
          setSelectedLanguage(currentLanguageOption.value);
        } else if (languageOptions.length > 0) {
          setSelectedLanguage(languageOptions[0].value);
        }
      } catch (error) {
        console.error("Error fetching languages:", error);
      }
    };

    fetchLanguages();
  }, [i18n.language]);

  const updateQuestionnaire = (
    key: string,
    { answer_ids, text_answer }: { answer_ids: number[]; text_answer?: string }
  ) => {
    setErrorQues(false);
    setQuestionnaire((prevQuestionnaire) => {
      const updatedQuestionnaire = { ...prevQuestionnaire };

      if (!updatedQuestionnaire[key]) {
        updatedQuestionnaire[key] = { answer_ids: [] };
      }

      const isMainQuestion = key === questions?.main_question?.key;

      if (isMainQuestion) {
        updatedQuestionnaire[key].answer_ids = answer_ids;

        if (text_answer) {
          updatedQuestionnaire[key].text_answer = text_answer;
        }

        Object.keys(updatedQuestionnaire).forEach((otherKey) => {
          if (otherKey !== key) {
            delete updatedQuestionnaire[otherKey]; // Видаляємо об'єкти для всіх інших питань
          }
        });
      } else {
        updatedQuestionnaire[key].answer_ids = answer_ids;

        if (text_answer) {
          updatedQuestionnaire[key].text_answer = text_answer;
        }
      }

      return updatedQuestionnaire;
    });
  };

  const renderForm = () => {
    const questionAnswer = questionnaire[questions!.main_question.key];

    if (!questionAnswer) return <></>;

    const options = questions!.main_question.options;

    const yesOption = options.find(
      (opt) =>
        opt.text.toLowerCase() === "yes" || opt.text.toLowerCase() === "так"
    );
    const noOption = options.find(
      (opt) =>
        opt.text.toLowerCase() === "no" || opt.text.toLowerCase() === "ні"
    );

    const yesOptionId = yesOption?.id;
    const noOptionId = noOption?.id;

    const selectedOptionId = questionAnswer.answer_ids[0];

    switch (selectedOptionId) {
      case yesOptionId:
        return (
          <Questionnaire1
            questionnaire={questionnaire}
            updateQuestionnaire={updateQuestionnaire}
            setDisabled={setDisabled}
          />
        );
      case noOptionId:
        return (
          <Questionnaire2
            questionnaire={questionnaire}
            updateQuestionnaire={updateQuestionnaire}
            setDisabled={setDisabled}
          />
        );
      default:
        return <></>;
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      for (const key in questionnaire) {
        if (questionnaire[key].answer_ids.length === 0) {
          setErrorQues(true);
          setLoading(false);
          return;
        }
      }
      await setProfileLanguage(selectedLanguage);
      await getProfileLanguage();
      const success = await sendAnswers(questionnaire);
      if (success.status === "success")
        localStorage.setItem("success", "success");
      if (error === "no report") {
        await makeReport();
        await fetchProfile();
      }
      setIsLoggedIn(true);
      onSubmit();
      setLoading(false);
      localStorage.removeItem("questions");
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  return (
    <Stack ta="start" gap="36px">
      {/* <Select
        label={
          <Text fw="bold" ta="left" mb={10}>
            {t("select_language")}
          </Text>
        }
        placeholder="Choose a language"
        data={languages}
        value={selectedLanguage}
        onChange={(value: string | null) => {
          if (value !== null) {
            setSelectedLanguage(value);
          }
        }}
        // searchable
      /> */}

      <RadioGroup
        w="100%"
        size="md"
        ta="left"
        label={
          <Text fw="bold" ta="left">
            {questions?.main_question?.text}
          </Text>
        }
        onChange={(value) => {
          const selectedId = parseInt(value, 10); // Перетворюємо значення на number
          updateQuestionnaire(questions!.main_question.key, {
            answer_ids: [selectedId], // Оновлюємо  answer_ids
          });
        }}
      >
        {questions?.main_question.options.map((option) => (
          <Radio
            key={option.id}
            mt="md"
            label={option.text}
            value={option.id.toString()} // Перетворюємо ID на string для value
            style={{ alignItems: "center" }}
          />
        ))}
      </RadioGroup>

      {renderForm()}
      {errorQues && (
        <Text color="red" size="sm">
          {t("missing_answers")}
        </Text>
      )}
      <Button
        fullWidth
        disabled={disabled}
        onClick={handleSubmit}
        loading={loading}
      >
        {t("submit_button")}
      </Button>
    </Stack>
  );
};
